/* eslint-disable */
// src/V2/data/exposeCampaign.ts
import { useEffect, useState } from 'react';

// The current Exposé campaign (name, banner) from NMT, so the home slider and
// the promo bar follow the campaign automatically instead of being edited by
// hand each month. One request per page load, shared by every caller.

export const EXPOSE_API_URL = 'https://nmt.loveworldapis.com/api/pcdl/expose/current';

export type ExposeCampaign = {
  name: string;
  bannerURL: string;
};

let cached: Promise<ExposeCampaign | null> | null = null;

/** Campaign name + banner, or null when there's no live campaign / the API is down. */
export function fetchExposeCampaign(): Promise<ExposeCampaign | null> {
  if (!cached) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    cached = fetch(EXPOSE_API_URL, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        // Current API: fields at the top level with statusCode (sent as the
        // string "200"); older: { status, data }.
        const payload = json?.data && typeof json.data === 'object' ? json.data : json;
        const ok = Number(json?.statusCode) === 200 || json?.status === true;
        const name = String(payload?.campaignName || '').trim();
        if (!ok || !name) return null;
        return { name, bannerURL: String(payload?.bannerURL || '').trim() };
      })
      .catch(() => null)
      .then((campaign) => {
        clearTimeout(timer);
        // Don't keep a failure around for the rest of the session.
        if (!campaign) cached = null;
        return campaign;
      });
  }
  return cached;
}

/** `undefined` while loading, `null` when unavailable, otherwise the campaign. */
export function useExposeCampaign(): ExposeCampaign | null | undefined {
  const [campaign, setCampaign] = useState<ExposeCampaign | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    fetchExposeCampaign().then((c) => {
      if (alive) setCampaign(c);
    });
    return () => {
      alive = false;
    };
  }, []);
  return campaign;
}
